import { viewerStrings } from '../copy/strings.js';
import { hasText, optionalText } from '../copy/text.js';
import { useState, useSyncExternalStore } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Alert from '@cloudscape-design/components/alert';
import { QueryError, queryLimits } from '@cdr-clarity/contracts';
import type { RulesState } from '../state/rules-state.js';
import { ClassificationRuleEditor } from './ClassificationRuleEditor.js';
import { RulesPreview } from './RulesPreview.js';

/** Native authoring controls; no classification or matching is evaluated here. */
export function RulesDialog({ state }: { state: RulesState }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [size, setSize] = useState(25);
  const snapshot = useSyncExternalStore(state.subscribe, state.getSnapshot);
  const rules = snapshot.draft.rules;
  const edit = (next: typeof rules) => {
    setError('');
    setNotice('');
    state.edit(next);
  };
  const run = async (operation: () => void | Promise<void>, success = '') => {
    setError('');
    setNotice('');
    try {
      await operation();
      setNotice(success);
    } catch (failure) {
      setError(
        failure instanceof QueryError
          ? `${failure.path}: ${failure.message}`
          : failure instanceof Error
            ? failure.message
            : String(failure),
      );
    }
  };
  const exportFile = () => {
    const url = URL.createObjectURL(new Blob([state.exportJson()], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cdr-clarity-classification-rules.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <>
      {hasText(viewerStrings.webComponentsRulesDialog.classificationRules) ? (
        <button type="button" onClick={() => setOpen(true)}>
          {viewerStrings.webComponentsRulesDialog.classificationRules}
        </button>
      ) : null}
      <Modal
        visible={open}
        onDismiss={() => setOpen(false)}
        header={optionalText(viewerStrings.webComponentsRulesDialog.classificationRules2)}
        size="large"
      >
        {hasText(viewerStrings.webComponentsRulesDialog.firstMatchingEnabledRuleWinsForEach) ? (
          <p>{viewerStrings.webComponentsRulesDialog.firstMatchingEnabledRuleWinsForEach}</p>
        ) : null}
        {hasText(error) && <Alert type="error">{error}</Alert>}
        {hasText(notice) && <Alert type="success">{notice}</Alert>}
        <fieldset disabled={snapshot.busy} style={{ border: 0, padding: 0 }}>
          <div className="field-tools" data-copy-group>
            {hasText(viewerStrings.webComponentsRulesDialog.addRule) ? (
              <button
                type="button"
                disabled={rules.length >= queryLimits.rules}
                onClick={() => void run(() => state.add())}
              >
                {viewerStrings.webComponentsRulesDialog.addRule}
              </button>
            ) : null}
            <label data-copy-label>
              {hasText(viewerStrings.webComponentsRulesDialog.importJSON) ? (
                <span>{viewerStrings.webComponentsRulesDialog.importJSON}</span>
              ) : null}
              <input
                type="file"
                accept=".json,application/json"
                aria-label={optionalText(
                  viewerStrings.webComponentsRulesDialog.importClassificationRulesJSON,
                )}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file)
                    void run(
                      () => state.importFile(file),
                      viewerStrings.webComponentsRulesDialog.rulesImportedAsADraftPreviewOr,
                    );
                }}
              />
            </label>
            {hasText(viewerStrings.webComponentsRulesDialog.exportJSON) ? (
              <button type="button" onClick={() => void run(exportFile)}>
                {viewerStrings.webComponentsRulesDialog.exportJSON}
              </button>
            ) : null}
          </div>
          {rules.map((rule, index) => (
            <ClassificationRuleEditor
              key={rule.id}
              rule={rule}
              position={index}
              count={rules.length}
              change={(next) => edit(rules.map((item, i) => (i === index ? next : item)))}
              remove={() => edit(rules.filter((_, i) => i !== index))}
              move={(offset) => {
                const next = [...rules];
                next.splice(index, 1);
                next.splice(index + offset, 0, rule);
                edit(next);
              }}
            />
          ))}
          <div className="field-tools" data-copy-group>
            {hasText(viewerStrings.webComponentsRulesDialog.validate) ? (
              <button
                type="button"
                onClick={() =>
                  void run(() => {
                    state.validate();
                  }, viewerStrings.webComponentsRulesDialog.rulesAreValid)
                }
              >
                {viewerStrings.webComponentsRulesDialog.validate}
              </button>
            ) : null}
            <label data-copy-label>
              {hasText(viewerStrings.webComponentsRulesDialog.previewRecords) ? (
                <span>{viewerStrings.webComponentsRulesDialog.previewRecords}</span>
              ) : null}
              <select
                aria-label={optionalText(
                  viewerStrings.webAccessibility.componentsRulesDialog2previewRecords,
                )}
                value={size}
                onChange={(event) => setSize(Number(event.target.value))}
              >
                {[25, 100, 500, 1000].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
            {hasText(viewerStrings.webComponentsRulesDialog.preview) ? (
              <button type="button" onClick={() => void run(() => state.preview(size))}>
                {viewerStrings.webComponentsRulesDialog.preview}
              </button>
            ) : null}
            {hasText(viewerStrings.webComponentsRulesDialog.applyAndSave) ? (
              <button
                type="button"
                onClick={() =>
                  void run(
                    () => state.apply(),
                    viewerStrings.webComponentsRulesDialog.rulesAppliedAndSavedInThisBrowser,
                  )
                }
              >
                {viewerStrings.webComponentsRulesDialog.applyAndSave}
              </button>
            ) : null}
          </div>
        </fieldset>
        {snapshot.busy &&
          (hasText(viewerStrings.webComponentsRulesDialog.processingClassificationRules) ? (
            <p role="status">
              {viewerStrings.webComponentsRulesDialog.processingClassificationRules}
            </p>
          ) : null)}
        {snapshot.preview && <RulesPreview preview={snapshot.preview} rules={rules} />}
      </Modal>
    </>
  );
}
